import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import SEOHead from './SEOHead';
import AgentTierMatrix from './ui/AgentTierMatrix';
import { useLivePricing, livePrice } from '../utils/pricingApi';

const { FiArrowLeft, FiCheck, FiX, FiZap, FiArrowRight } = FiIcons;

/* Plan feature/metadata is static content; PRICES are resolved live from
 * Chargebee at render time (key = lowercased plan name). Never hardcode prices. */
const plans = [
  {
    name: 'Author',
    cadence: 'per month',
    blurb: 'For authors writing and publishing in earnest.',
    cta: 'Start Author',
    highlight: true,
    features: ['Unlimited books & words', 'Full export — EPUB, PDF, DOCX, KDP', 'Live KDP Preflight', 'Author Voice DNA (up to 3 books)', 'Version history', 'Email support']
  },
  {
    name: 'Pro',
    cadence: 'per month',
    blurb: 'For ghostwriters, teams, and power users.',
    cta: 'Go Pro',
    highlight: false,
    features: ['Everything in Author', 'Advanced AI models', 'Social book marketing (autopilot)', 'Collaboration — editors & co-authors', 'Audiobook narration add-on', 'API access', 'Priority support']
  }
];

const PricingPage = () => {
  const pricing = useLivePricing();

  // JSON-LD deliberately omits numeric prices until they are live from Chargebee,
  // so we never publish an invented/hardcoded price into structured data.
  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: 'ManuscriptSmith',
    description: 'AI writing studio with Author Voice DNA and live KDP Preflight, plus an optional audiobook narration add-on.'
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-parchment-50 to-parchment-100">
      <SEOHead
        title="Pricing - Author $19, Pro $49 | ManuscriptSmith"
        description="Simple, transparent pricing for ManuscriptSmith. Author is $19/mo for unlimited books with Author Voice DNA and live KDP Preflight, or $49/mo Pro with collaboration and API."
        keywords="manuscriptsmith pricing, ai writing software pricing, kdp writing tool cost, scrivener alternative pricing"
        canonicalUrl="https://manuscriptsmith.com/pricing"
        structuredData={structuredData}
      />

      <main>
        <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-10 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Simple, <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary-500 to-secondary-500">transparent</span> pricing
          </h1>
          <p className="text-xl text-gray-600">Pick the plan that fits your book. Cancel anytime.</p>
        </section>

        {/* Plans */}
        <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
          {/* price TBD (research job running): entry plan removed; paid plans only */}
          <div className="grid md:grid-cols-2 gap-8 items-start max-w-3xl mx-auto">
            {plans.map((plan, i) => (
              <motion.div
                key={plan.name}
                className={`rounded-2xl p-8 shadow-lg ${plan.highlight ? 'bg-gradient-to-br from-primary-500 to-secondary-500 text-white md:scale-105 relative' : 'bg-white border border-gray-200'}`}
                initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                transition={{ duration: 0.6, delay: i * 0.1 }}
              >
                {plan.highlight && (
                  <div className="absolute -top-3 right-6 bg-yellow-400 text-gray-900 text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wide">Most Popular</div>
                )}
                <h2 className={`text-2xl font-bold mb-1 ${plan.highlight ? 'text-white' : 'text-gray-900'}`}>{plan.name}</h2>
                <div className="flex items-end gap-2 mb-1">
                  {(() => {
                    const price = livePrice(pricing, plan.name);
                    return price ? (
                      <span className="text-4xl font-bold">{price}</span>
                    ) : (
                      <span className={`text-2xl font-bold ${plan.highlight ? 'opacity-80' : 'text-gray-400'}`} title="Live pricing from Chargebee">
                        {pricing.status === 'loading' ? 'Loading…' : 'Coming soon'}
                      </span>
                    );
                  })()}
                  <span className={`mb-1 text-sm ${plan.highlight ? 'opacity-90' : 'text-gray-500'}`}>{plan.cadence}</span>
                </div>
                <p className={`mb-6 text-sm ${plan.highlight ? 'opacity-90' : 'text-gray-500'}`}>{plan.blurb}</p>
                <ul className="space-y-3 mb-8">
                  {plan.features.map((f) => (
                    <li key={f} className="flex items-start">
                      <SafeIcon icon={FiCheck} className={`w-5 h-5 mr-2 flex-shrink-0 ${plan.highlight ? 'text-yellow-300' : 'text-primary-500'}`} />
                      <span className={plan.highlight ? 'text-white' : 'text-gray-700'}>{f}</span>
                    </li>
                  ))}
                </ul>
                <Link
                  to="/signup"
                  className={`w-full inline-flex justify-center items-center py-3 font-bold rounded-lg transition-colors ${plan.highlight ? 'bg-white text-primary-600 hover:bg-gray-50' : 'bg-primary-500 text-white hover:bg-primary-600'}`}
                >
                  {plan.cta}
                </Link>
              </motion.div>
            ))}
          </div>
          <p className="text-center text-sm text-gray-500 mt-6">
            <SafeIcon icon={FiZap} className="w-4 h-4 inline mr-1 text-primary-500" />
            Audiobook narration is an optional add-on via AudiobookSmith, our sibling product.
          </p>
        </section>

        {/* What's in each plan — data-driven agent x tier matrix from the registry */}
        <section className="bg-white py-16">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-10">
              <h2 className="text-3xl font-bold text-gray-900 mb-3">What&apos;s in each plan</h2>
              <p className="text-lg text-gray-600">Every ManuscriptSmith agent, by plan. Green means included; add-ons are priced where they apply.</p>
            </div>
            <AgentTierMatrix />
            <p className="text-center text-sm text-gray-500 mt-8">
              Want the full story on any agent? <Link to="/capabilities" className="text-primary-600 font-semibold hover:underline">Browse all capabilities →</Link>
            </p>
          </div>
        </section>

        {/* CTA */}
        <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="bg-gradient-to-r from-primary-500 to-secondary-500 rounded-2xl p-10 text-center text-white shadow-xl">
            <h2 className="text-3xl font-bold mb-4">Start writing in your voice today</h2>
            <p className="text-lg opacity-90 mb-8">See the plans and get started.</p>
            <Link to="/signup" className="inline-flex items-center px-8 py-4 bg-white text-primary-600 font-bold text-lg rounded-lg hover:bg-gray-50 transition-all">
              Get started <SafeIcon icon={FiArrowRight} className="w-5 h-5 ml-2" />
            </Link>
          </div>
        </section>
      </main>
    </div>
  );
};

export default PricingPage;
